import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DragResizeSidePanel } from '@/shared/components';
import { useSession } from '@/hooks/useSession';
import { useCustomerRequest } from '../hooks/useCustomerRequests';
import { CreateRequestForm } from './CreateRequestForm';
import { RequestDetailBody } from './RequestDetailBody';
import type { AskDraft } from '../types';

interface Props {
  /** 'new' opens the create form; a request id opens its detail; null closes the panel. */
  selected: 'new' | string | null;
  onSelect: (selected: 'new' | string | null) => void;
  canChangeStatus: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
}

export function CustomerRequestPanel({ selected, onSelect, canChangeStatus, containerRef }: Props) {
  const { user } = useSession();
  const [draft, setDraft] = useState<(AskDraft & { requestId: string }) | null>(null);
  const requestId = selected && selected !== 'new' ? selected : null;
  const { data: request, isLoading, isError } = useCustomerRequest(requestId);
  const heading = selected === 'new' ? 'New request' : request?.title ?? '';

  return (
    <DragResizeSidePanel
      open={!!selected}
      onClose={() => onSelect(null)}
      storageKey="atek_customer_requests_panel_width"
      containerRef={containerRef}
      minWidth={380}
    >
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 shrink-0 flex items-start justify-between gap-2">
        <h2 className="text-sm font-semibold min-w-0 break-words">{heading}</h2>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 -mr-1" onClick={() => onSelect(null)} aria-label="Close panel">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {selected === 'new' && (
          <CreateRequestForm
            key="new"
            onDone={(id) => onSelect(id)}
            onUpvoteExisting={(id, d) => { setDraft({ ...d, requestId: id }); onSelect(id); }}
          />
        )}
        {requestId && isLoading && (
          <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        )}
        {requestId && isError && <p className="p-4 text-sm text-muted-foreground">This request could not be loaded. It may have been deleted.</p>}
        {request && requestId === request.id && (
          <RequestDetailBody
            key={request.id}
            request={request}
            initialAsk={draft?.requestId === request.id ? draft : undefined}
            meId={user?.pg_id}
            canChangeStatus={canChangeStatus}
            onDeleted={() => onSelect(null)}
          />
        )}
      </div>
    </DragResizeSidePanel>
  );
}
